import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Bell, Target, CheckSquare, Users, X, Clock, BellRing, AlarmClock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  goal: Target,
  task: CheckSquare,
  contact: Users
};

const typeColors = {
  goal: 'text-amber-600 bg-amber-100',
  task: 'text-emerald-600 bg-emerald-100',
  contact: 'text-pink-600 bg-pink-100'
};

const typeLabels = {
  goal: 'Goal',
  task: 'Task',
  contact: 'Follow-up'
};

const SNOOZE_CHOICES = [
  { value: '10min', label: '10 minutes' },
  { value: '1hour', label: '1 hour' },
  { value: 'tomorrow', label: 'Tomorrow morning (9 AM)' },
];

export const NotificationCenter = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({ due_now: [], upcoming: [], total_due: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const shownToastsRef = useRef(new Set());
  const prevDueCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/notifications/upcoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setNotifications(data);
      
      // Show toast notifications for new due items
      if (data.due_now && data.due_now.length > 0) {
        data.due_now.forEach(item => {
          const toastKey = `${item.type}-${item.id}-${item.scheduled_at_utc || ''}`;
          if (!shownToastsRef.current.has(toastKey)) {
            shownToastsRef.current.add(toastKey);
            // Use simple string toast instead of JSX to avoid babel issues
            toast.info(`${typeLabels[item.type]} Reminder: ${item.title}`, {
              duration: 8000,
              description: item.description || 'Click to view all reminders'
            });
          }
        });
        
        // Indicate new notifications if count increased
        if (data.total_due > prevDueCountRef.current) {
          setHasNewNotifications(true);
        }
      }
      
      prevDueCountRef.current = data.total_due;
      
      // Request browser notification permission and show native notification
      if (data.total_due > 0 && typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          // Only show native notifications for first 2 new items
          data.due_now.slice(0, 2).forEach(item => {
            const nativeKey = `native-${item.type}-${item.id}-${item.scheduled_at_utc || ''}`;
            if (!shownToastsRef.current.has(nativeKey)) {
              shownToastsRef.current.add(nativeKey);
              try {
                new Notification(`${typeLabels[item.type]} Reminder`, {
                  body: item.title,
                  icon: '/favicon.ico',
                  tag: nativeKey
                });
              } catch (e) {
                console.log('Native notification failed:', e);
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications]);

  // Clear "new" indicator when opening
  useEffect(() => {
    if (isOpen) {
      setHasNewNotifications(false);
    }
  }, [isOpen]);

  const dismissReminder = async (type, id) => {
    try {
      const response = await axios.post(`${API}/notifications/dismiss/${type}/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Dismiss response:', response.data);
      // Remove from shown toasts so it can show again if re-added
      shownToastsRef.current.delete(`${type}-${id}`);
      shownToastsRef.current.delete(`native-${type}-${id}`);
      // Immediately update local state to remove the item
      setNotifications(prev => ({
        ...prev,
        due_now: prev.due_now?.filter(item => !(item.type === type && item.id === id)) || [],
        upcoming: prev.upcoming?.filter(item => !(item.type === type && item.id === id)) || [],
        total_due: Math.max(0, (prev.total_due || 0) - 1),
        total_upcoming: Math.max(0, (prev.total_upcoming || 0) - 1)
      }));
      toast.success('Reminder dismissed');
      // Also refetch to ensure sync with server
      setTimeout(() => fetchNotifications(), 500);
    } catch (error) {
      console.error('Dismiss error:', error);
      toast.error('Failed to dismiss reminder');
    }
  };

  const snoozeReminder = async (item, option) => {
    try {
      await axios.post(`${API}/reminders/${item.reminder_id}/snooze`, { option }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const label = SNOOZE_CHOICES.find((c) => c.value === option)?.label.toLowerCase();
      toast.success(`Snoozed for ${label}`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    }
  };

  const requestNotificationPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          toast.success('Browser notifications enabled!');
        }
      } catch (e) {
        console.log('Permission request failed:', e);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    const dateObj = new Date(date + 'T00:00:00');
    
    if (date === today) return 'Today';
    if (date === tomorrow) return 'Tomorrow';
    
    // Include year if not current year
    const currentYear = new Date().getFullYear();
    const dateYear = dateObj.getFullYear();
    
    if (dateYear === currentYear) {
      return dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return '';
    const parts = time.split(':');
    if (parts.length < 2) return time;
    const hour = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDateTime = (date, time) => {
    const dateStr = formatDate(date);
    const timeStr = formatTime(time);
    if (dateStr && timeStr) {
      return `${dateStr} at ${timeStr}`;
    }
    return dateStr || timeStr || 'No date set';
  };

  const ROUTES = { task: '/tasks', contact: '/contacts', goal: '/goals', event: '/calendar' };
  const goToItem = (item) => {
    const path = ROUTES[item.source_type || item.type];
    if (path) { setIsOpen(false); navigate(path); }
  };

  const renderNotificationItem = (item, isDue) => {
    const Icon = typeIcons[item.type] || Bell;
    const colors = typeColors[item.type] || 'text-gray-600 bg-gray-100';
    const bgClass = isDue 
      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
      : 'bg-muted/50';
    
    return (
      <motion.div
        key={`${item.type}-${item.id}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className={`flex items-start gap-3 p-3 mb-2 rounded-lg cursor-pointer hover:brightness-95 ${bgClass}`}
        onClick={() => goToItem(item)}
        data-testid={`reminder-item-${item.type}-${item.id}`}
      >
        <div className={`p-2 rounded-lg ${colors}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{item.title}</p>
          <div className="flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              {formatDateTime(item.reminder_date, item.reminder_time)}
            </p>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            {typeLabels[item.type] || item.type}
            {item.offset_label ? ` • ${item.offset_label}` : (item.category ? ` • ${item.category}` : '')}
          </p>
          {!isDue && item.snoozed_until && item.snoozed_until === item.scheduled_at_utc && (
            <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`snoozed-until-${item.type}-${item.id}`}>
              <AlarmClock className="w-3 h-3" />
              Snoozed until {new Date(item.snoozed_until).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
          {item.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{item.description}</p>
          )}
        </div>
        {isDue && item.reminder_id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 shrink-0 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                onClick={(e) => e.stopPropagation()}
                data-testid={`snooze-${item.type}-${item.id}`}
              >
                <AlarmClock className="w-4 h-4 text-amber-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuLabel className="text-xs">Snooze for</DropdownMenuLabel>
              {SNOOZE_CHOICES.map((c) => (
                <DropdownMenuItem
                  key={c.value}
                  onSelect={() => snoozeReminder(item, c.value)}
                  data-testid={`snooze-option-${c.value}-${item.id}`}
                >
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button 
          size="icon" 
          variant="ghost" 
          className={`h-8 w-8 shrink-0 ${isDue ? 'hover:bg-red-100 dark:hover:bg-red-900/50' : 'hover:bg-muted'}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dismissReminder(item.type, item.id);
          }}
          data-testid={`dismiss-${item.type}-${item.id}`}
        >
          <X className="w-4 h-4" />
        </Button>
      </motion.div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
          onClick={() => {
            requestNotificationPermission();
            setIsOpen(true);
          }}
        >
          {hasNewNotifications || notifications.total_due > 0 ? (
            <BellRing className="w-5 h-5 text-red-500" />
          ) : (
            <Bell className="w-5 h-5" />
          )}
          {notifications.total_due > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {notifications.total_due > 9 ? '9+' : notifications.total_due}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Reminders
            {notifications.total_due > 0 && (
              <span className="text-sm font-normal text-red-500">
                ({notifications.total_due} due)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-2">
          {notifications.total_due === 0 && (!notifications.upcoming || notifications.upcoming.length === 0) ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No reminders set</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add reminders to your goals, tasks, contacts, or events
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.total_due > 0 && notifications.due_now && (
                <div>
                  <h3 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Due Now ({notifications.total_due})
                  </h3>
                  <AnimatePresence>
                    {notifications.due_now.map((item) => renderNotificationItem(item, true))}
                  </AnimatePresence>
                </div>
              )}
              
              {notifications.upcoming && notifications.upcoming.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2">
                    Upcoming ({notifications.total_upcoming || notifications.upcoming.length})
                  </h3>
                  {notifications.upcoming.map((item) => renderNotificationItem(item, false))}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationCenter;
